import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import {
  cloneDeep,
  compact,
  debounce,
  isEqual,
  mapValues,
  omit,
  sortBy,
  trim,
  uniqueId,
} from 'lodash';

import {
  convertHtmlContentToPlainText,
  replaceFilePlaceholders,
  replacePromptTags,
} from 'src/components/RichSlate';
import {
  customerCanChangeGenerationLanguage,
  customerCanChangeNumberOfVariations,
} from 'src/data/accessRights';
import { isNotTalkingPointAnnotation } from 'src/data/annotation';
import { getAssetLabel } from 'src/data/asset';
import {
  BRIEF_TYPE,
  checkBriefHasContent,
  EMPTY_FIELD_ERROR,
  generateBriefCacheKey,
  getRecomposeTextFromBrief,
  isEnoughContentForExtraction,
  mapBriefFromGenerationResult,
  mapExtractedContentForEditing,
  validateDocumentBrief,
  validateFreestyleBrief,
} from 'src/data/brief';
import { FIELD_TYPE, MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import {
  AI_SUGGESTION_MODE,
  alignLanguage,
  DEFAULT_LANGUAGE,
  DEFAULT_NUMBER_OF_VARIATIONS,
  EMPTY_ALL_INSTRUCTIONS_DATA,
  GENERATION_GUIDANCE_TYPE,
  GENERATION_IMPROVE_MODE,
  GENERATION_MODE_TYPE,
  getAlignedSuggestionMode,
  getDocumentDefaultNumberOfVariations,
  getGenerationImproveParams,
  getLanguageTargetFormality,
  isNonEnglish,
  isRecomposeMode,
  isStructureInstructionsPresent,
  LANGUAGE,
  normalizeLanguage,
  shouldImproveScoresForMode,
} from 'src/data/generation';
import {
  alignToolForGeneration,
  doesToolHasMultipleGenerationModes,
  doesToolSupportBackgroundUrlBriefExtraction,
  doesToolSupportCampaignKeywords,
  doesToolSupportPromptInstruction,
  doesToolSupportsSubTemplates,
  GENERATION_TOOL,
  getToolBriefType,
  getToolDefaultAssetType,
  getToolDefaultGenerationMode,
  getToolGenerationModes,
  getToolLabel,
  getToolMaxCampaignKeywords,
  getToolMaxSuggestKeywords,
  getToolMaxWordsPerKeyword,
  getToolMixtureSubAssetTypeSubtitles,
  getToolMixtureSubGenerationTools,
  getToolMixtureSubGenerationToolsModeOverride,
  isBasicContentImproverTool,
  isBasicGenerationTool,
  isFreestyleGenerationTool,
  isImageGenerationTool,
  isLandingSeoMetaDataGenerationTool,
  isMixtureTool,
  isMultiPromptTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import {
  isImageResource,
  isNonImageFileResource,
  mimicImageResource,
  USER_ANNOTATIONS_ORIGIN,
} from 'src/data/performance';
import {
  getProjectLastUsedGenerationInfo,
  getProjectLatestGenerationResult,
  isBareboneProject,
  PROJECT_TYPE,
} from 'src/data/project';
import { SUGGEST_PROMPT_TYPES } from 'src/data/prompt';
import {
  getShortFormContentTypeTool,
  SHORT_FORM_CONTENT_TYPE,
  shortFormContentTypeHasSectionNumbering,
} from 'src/data/shortForm';
import { PROMPT_SUB_TEMPLATE_ID } from 'src/data/template';
import {
  doesToolSupportsPersonaForMode,
  doesToolSupportSuggestKeywordsForMode,
} from 'src/data/utils';
import { validateUrl } from 'src/data/validation';
import { extractBrief } from 'src/graphql/brief';
import { uploadCsvFile } from 'src/graphql/customerMultiPromptTemplates';
import { updateProjectPersonas } from 'src/graphql/customerPersona';
import { suggestPrompt } from 'src/graphql/generation/suggestPrompt';
import { generationToolRatings } from 'src/graphql/generationTool';
import { waitForJob } from 'src/graphql/job';
import { suggestQuestionsForText } from 'src/graphql/longFormBuilder/suggestQuestionsForText';
import { extractAssetsFromResource } from 'src/graphql/resource/extractAssetsFromResource';
import { queryLongFormToolsForContentType } from 'src/graphql/shortForm';
import { getQueryParamValue } from 'src/lib/browser';
import { parseJson } from 'src/lib/common';
import { pluralize } from 'src/lib/format';
import { parseErrors } from 'src/lib/graphql';
import { isHtmlContent } from 'src/lib/html';
import * as ls from 'src/lib/localData';
import { captureMessage } from 'src/lib/sentry';
import { countWords } from 'src/lib/string';
import { areAllUrlsExtracted, extractUrls, getSuccessfulExtractions } from 'src/lib/url';
import { ROUTE_PARAMS } from 'src/routes';
import { waitForBriefExtraction } from 'src/services';

import { INFINITY_EXTENSION_MODE, SINGLE_PROMPT_ID } from '../const';
import {
  contentInputModeFromContentType,
  getSectionToolFromAssetType,
  IMPROVER_CONTENT_INPUT_MODE,
  isRewriteSectionEmpty,
  isRewriteSectionSupportHtml,
  mergeRewriteSectionsIntoHtml,
  splitHtmlToRewriteSections,
} from '../lib/contentImprover';
import { serializeMultiPromptFields, serializeToolPrompts } from '../utils';
import { getStepIndex, MULTI_PROMPT_TEMPLATE_STEPS } from '../views/MultiPromptTemplatePopup/const'; // TODO: move these constants higher, "stores" should not import anything from "views"

import { filterVisibleDDTemplates } from './utils';

const MAX_WORDS_PER_CALL_TO_ACTION = 10;
const LANGUAGE_SOURCE = {
  other: 'other',
  user: 'user',
};

const EMPTY_FREESTYLE_CONTENT = {
  id: null,
  name: '',
  allInstructions: '',
  allInstructionsData: null,
  personalizedPrompt: false,
  improvedPrompt: false,
};

const EMPTY_SUB_TEMPLATE_PROPERTIES = {
  id: null,
  qaInputs: [],
};

const INITIAL_ADVANCED_SETTINGS_VIEW_PROPS = {
  view: null,
  title: '',
  showBackButton: false,
};

const INITIAL_STATE = {
  // TODO: this state mainly duplicates the MainStore, consider removing this one and fully rely on `generationToolListId`
  generationTool: null,
  generationToolOverride: null, // Temporary override for generation (e.g., from template classification)
  customMultiPromptId: null,
  subTemplateId: null,
  templateDataVersion: 0,

  subGenerationToolsProps: {},
  briefContent: null,
  briefContentCache: {},
  freestyleToolContent: EMPTY_FREESTYLE_CONTENT,
  isSuggestPromptLoading: false,

  subTemplateProperties: EMPTY_SUB_TEMPLATE_PROPERTIES,

  initialFreestyleToolContent: null,
  initialSubTemplateProperties: null,

  rewriteContentType: null,
  rewriteInputMode: null,
  rewriteInputSections: [],
  rewriteOutputSections: [],
  rewriteInputToOutputSectionsMap: new Map(),
  loadingRewriteInputSections: false,
  rewriteProcessingSectionsIds: new Set(),
  rewriteImproveMode: null, // value gets initiated in `BlockInstructions`
  rewriteText: '',
  rewriteInstructions: undefined,
  uploadingResource: false,
  extractingResourceAssets: false,

  availableDDTemplates: [],
  visibleDDTemplates: [],
  selectedModeId: null,
  selectedModeType: null,

  selectedCustomerPersona: null,
  selectedCustomerPersonaCache: null,
  isUpdatingProjectPersonas: false,

  newAnnotationText: '',
  newCampaignKeywordsText: '',
  annotations: [],

  isImageGenerationEnabled: false,

  isUpdatingProductAttributes: false,
  isFreestyleToolSaveViewVisible: false,
  isFreestyleToolConfirmChangesViewVisible: false,

  numberOfVariations: DEFAULT_NUMBER_OF_VARIATIONS,
  language: DEFAULT_LANGUAGE,
  languageSource: LANGUAGE_SOURCE.other,

  errors: null,
  briefError: {},
  keywordsError: {},
  generating: false,
  disabledGeneration: false,

  extractedBriefs: {}, // url => content
  extractedBriefIds: {}, // url => id
  currentUrls: [],
  extractedBriefErrors: {},

  errorBriefUrl: null,
  errorBriefContent: null,
  errorBriefGeneral: null,
  errorBriefGeneralCode: null,
  extractedBrief: {},
  isExtractingBrief: false,
  shouldWaitForBrief: false,
  briefExtractionsInProgress: {},
  showImportFromUrl: false,
  popoverContent: { body: '' },

  showAdvancedOptions: false,
  generationToolOptionsCache: {},

  isCustomFormulasVisible: false,
  callToAction: '',

  // the "mode" in which `CanvasProject` is used in the "Infinity" extension context,
  // currently it is always "prompt generation" mode
  infinityExtensionMode: INFINITY_EXTENSION_MODE.promptGeneration,

  isManagePersonasPopupOpen: false,

  messagingBank: [],
  attachedResourcesMap: {},

  uploadedCsv: null,
  isCsvLoading: false,

  preSuggestedPromptContent: '',

  structureInstructionsData: null,
  lastSelectedBrandVocabularyIds: [],

  questions: [],
  isQuestionPopoverOpen: false,
  isSuggestPromptQuestionsLoading: false,
  allInstructionsDataPerQuestion: {},
  /* --------------------------------------------------------------- */

  /* MULTI PROMPT TEMPLATE FIELDS */
  toolPrompts: null, // TODO: check if we need to deprecate
  multiPromptActiveStep: MULTI_PROMPT_TEMPLATE_STEPS[0], // TODO: check if we need to deprecate
  multiPromptFields: null,
  activeSubToolBlock: null,
  isSubToolsExpanded: false,

  // 2 copies in order to apply changes only if user click Apply button in Advanced Settings Popover
  // and to reset of change were not applied
  multiPromptTemplateAdvancedSettings: null,
  multiPromptTemplateAdvancedSettingsDraft: null,

  /* ADVANCED SETTINGS DRAFT */
  advancedSettingsDraft: null,

  /* ADVANCED SETTINGS POPOVER FIELDS */
  advancedSettingsActiveViewProps: INITIAL_ADVANCED_SETTINGS_VIEW_PROPS,
};

const setError =
  (field, error) =>
  ({ getState, setState }) => {
    const { errors } = getState();
    setState({ errors: { ...errors, [field]: error } });
  };

const validateCallToActionText = (value) => {
  if (countWords(value) > MAX_WORDS_PER_CALL_TO_ACTION) {
    return {
      valid: false,
      message: `Call To Action can be maximum ${MAX_WORDS_PER_CALL_TO_ACTION} words.`,
    };
  }

  return { valid: true };
};

const toolCacheKey = (generationTool, customMultiPromptId, subTemplateId) => {
  if (customMultiPromptId) {
    return generationTool + customMultiPromptId;
  }

  if (doesToolSupportsSubTemplates(generationTool)) {
    if (!subTemplateId) {
      return;
    }

    return `${generationTool}_${subTemplateId}`;
  }

  return generationTool;
};

const alignRewriteOutputSectionsDebounced = debounce(({ getState, setState }) => {
  const { rewriteContentType, rewriteInputSections, rewriteInputToOutputSectionsMap } = getState();

  rewriteInputToOutputSectionsMap.clear();

  const clonedSections = cloneDeep(rewriteInputSections);
  const rewriteOutputSections = [];

  clonedSections.forEach((section) => {
    // if it's not an HTML section, just use it directly
    if (!isRewriteSectionSupportHtml(rewriteContentType, section)) {
      rewriteOutputSections.push(section);
      return;
    }

    // split HTML section content to a list of text/image sections
    const sectionsFromHtml = splitHtmlToRewriteSections(section.generationTool, section.content);
    rewriteOutputSections.push(...sectionsFromHtml);
    rewriteInputToOutputSectionsMap.set(
      section.id,
      sectionsFromHtml.map(({ id }) => id)
    );
  });

  setState({ rewriteOutputSections });
}, 300);

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState, dispatch }, { project }) => {
      const firstPersona =
        project?.customerPersonas?.length > 0 ? project.customerPersonas[0] : null;

      setState({
        ...cloneDeep(INITIAL_STATE),
        selectedCustomerPersona: firstPersona,
        annotations: project?.annotations || [],
      });

      dispatch(PRIVATE_ACTIONS.initGenerationLanguage());
      dispatch(PRIVATE_ACTIONS.initFromGenerationResult());
      dispatch(ACTIONS.initSubGenerationToolsProps());
      dispatch(PRIVATE_ACTIONS.initFreestyleToolFromGenerationResult());
      dispatch(ACTIONS.shouldExtractUrlBrief());

      const customMultiPromptIdToolFromUrl = getQueryParamValue(ROUTE_PARAMS.customMultiPromptId);
      if (customMultiPromptIdToolFromUrl) {
        setState({ customMultiPromptId: customMultiPromptIdToolFromUrl });
      }
    },

  update: () => () => {},

  getCampaignKeywordsAnnotations:
    () =>
    ({ getState }) =>
      getState().annotations.filter(
        (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.campaignKeyword
      ),

  getRegularAnnotations:
    () =>
    ({ getState }) =>
      getState().annotations.filter(
        (annotation) =>
          annotation.type !== GENERATION_GUIDANCE_TYPE.campaignKeyword &&
          annotation.type !== GENERATION_GUIDANCE_TYPE.hashtag
      ),

  initGenerationLanguage:
    () =>
    ({ getState, setState }, { customer, project }) => {
      const { languageSource } = getState();

      // If generation language was already changed by user - don't rewrite it
      if (languageSource === LANGUAGE_SOURCE.user) {
        return;
      }

      // Customer might not be allowed to change the generation language
      if (!customerCanChangeGenerationLanguage(customer)) {
        setState({
          language: DEFAULT_LANGUAGE,
          languageSource: LANGUAGE_SOURCE.other,
        });
        return;
      }

      // Language from the last generation request
      const latestResult = getProjectLatestGenerationResult(project);
      const lastUsedLanguage = alignLanguage(
        latestResult?.language,
        latestResult?.languageTargetFormality
      );

      // Last user selected language
      const lastSelectedLanguage = alignLanguage(ls.documentGenerationLanguage.read());

      const isLastUsedLanguageInList = LANGUAGE[lastUsedLanguage]
        ? lastUsedLanguage
        : DEFAULT_LANGUAGE;

      const languageOrder = isLastUsedLanguageInList
        ? lastUsedLanguage || lastSelectedLanguage
        : lastSelectedLanguage;

      setState({
        language: languageOrder || DEFAULT_LANGUAGE,
        languageSource: LANGUAGE_SOURCE.other,
      });
    },

  // TODO: check if we need to init tool from results here, or getting it from the MainStore is enough
  initFromGenerationResult:
    () =>
    ({ setState, dispatch }, { project, isApplicationModeInfinity }) => {
      // We don't care about data derived from "generation results" when working in Infinity context
      if (isApplicationModeInfinity) {
        return;
      }

      const latestResult = getProjectLatestGenerationResult(project);
      if (!latestResult) {
        return;
      }

      const lastUsedInfo = getProjectLastUsedGenerationInfo(project);
      if (isFreestyleGenerationTool(lastUsedInfo?.generationTool)) {
        setState({ generationTool: GENERATION_TOOL.freestyle });
      } else {
        const briefContent = mapBriefFromGenerationResult(
          lastUsedInfo?.generationTool,
          latestResult
        );

        setState({
          generationTool: lastUsedInfo?.generationTool,
          customMultiPromptId: lastUsedInfo?.customMultiPromptId,
          briefContent,
        });

        dispatch(PRIVATE_ACTIONS.storeBriefContentInCache());
      }
    },

  initFreestyleToolFromGenerationResult:
    () =>
    ({ setState }, { project }) => {
      const latestResult = getProjectLatestGenerationResult(project);
      if (!latestResult) {
        return;
      }

      const lastUsedInfo = getProjectLastUsedGenerationInfo(project);
      if (isFreestyleGenerationTool(lastUsedInfo?.generationTool)) {
        setState({
          freestyleToolContent: {
            preloadId: latestResult.freestyleToolId,
            allInstructions: latestResult.freestyleToolAllInstructions,
            allInstructionsData: parseJson(latestResult.freestyleToolAllInstructionsData),
          },
        });
      }
    },

  validateRewrite:
    () =>
    ({ getState, dispatch }) => {
      const { rewriteOutputSections, rewriteImproveMode, rewriteInstructions } = getState();

      if (!rewriteOutputSections.some((section) => !isRewriteSectionEmpty(section))) {
        dispatch(setError('rewrite', 'Input needed'));
        return false;
      }

      if (
        rewriteImproveMode === GENERATION_IMPROVE_MODE.instructions &&
        trim(rewriteInstructions).length === 0
      ) {
        dispatch(setError('rewriteManualInstructions', EMPTY_FIELD_ERROR));
        return false;
      }

      return true;
    },

  validateAnnotationText:
    (value, type) =>
    ({ getState }) => {
      const { generationTool } = getState();
      // Validate new annotation content
      const maxWordsPerKeyword = getToolMaxWordsPerKeyword(generationTool);
      if (maxWordsPerKeyword && countWords(value) > maxWordsPerKeyword) {
        const postfix = pluralize(maxWordsPerKeyword, 'word', 'words');
        let itemTypeLabel;
        switch (type) {
          case GENERATION_GUIDANCE_TYPE.campaignKeyword:
            itemTypeLabel = 'Campaign keywords';
            break;
          default:
            itemTypeLabel = 'Talking points';
        }
        return {
          valid: false,
          message: `${itemTypeLabel} can be only ${maxWordsPerKeyword} ${postfix} long for this template`,
        };
      }

      return { valid: true };
    },

  validateNewAnnotation:
    (content, type) =>
    ({ getState, dispatch }) => {
      const { generationTool } = getState();

      // Do not add empty annotations
      if (content.length === 0) {
        return { valid: false };
      }

      // Skip validations for Infinity "prompt" mode
      if (dispatch(ACTIONS.inExtensionPromptMode())) {
        return { valid: true };
      }

      // Check if user still can "select" annotations
      const isCampaignKeyword = type === GENERATION_GUIDANCE_TYPE.campaignKeyword;
      const campaignAnnotations = dispatch(PRIVATE_ACTIONS.getCampaignKeywordsAnnotations());
      const regularAnnotations = dispatch(PRIVATE_ACTIONS.getRegularAnnotations());
      const maxKeywordsCount = isCampaignKeyword
        ? getToolMaxCampaignKeywords(generationTool)
        : getToolMaxSuggestKeywords(generationTool) || 1;

      const campaignKeywordsExceedingLimit =
        isCampaignKeyword &&
        campaignAnnotations.length >= getToolMaxCampaignKeywords(generationTool);

      const regularKeywordsExceedingLimit =
        !isCampaignKeyword &&
        regularAnnotations.length >= getToolMaxSuggestKeywords(generationTool);

      if (campaignKeywordsExceedingLimit || regularKeywordsExceedingLimit) {
        const postfix = pluralize(maxKeywordsCount, 'input is', 'inputs are');
        return {
          valid: false,
          message: `Only ${maxKeywordsCount} ${postfix} supported`,
        };
      }

      // Validate new annotation content
      const wordsValidation = dispatch(PRIVATE_ACTIONS.validateAnnotationText(content, type));
      if (!wordsValidation.valid) {
        return wordsValidation;
      }

      return { valid: true };
    },

  storeBriefContentInCache:
    () =>
    ({ getState, setState }) => {
      const { briefContent, briefContentCache, generationTool } = getState();
      const cacheKey = generateBriefCacheKey(generationTool);
      setState({
        briefContentCache: {
          ...briefContentCache,
          [cacheKey]: briefContent,
        },
      });
    },

  storeCustomerPersonaInCache:
    () =>
    ({ getState, setState }) => {
      const { selectedCustomerPersona } = getState();
      setState({
        selectedCustomerPersona: null,
        selectedCustomerPersonaCache: selectedCustomerPersona,
      });
    },

  restoreCustomerPersonaFromCache:
    () =>
    ({ getState, setState }) => {
      const { selectedCustomerPersonaCache } = getState();
      if (selectedCustomerPersonaCache) {
        setState({
          selectedCustomerPersonaCache: null,
          selectedCustomerPersona: selectedCustomerPersonaCache,
        });
      }
    },

  alignNumberOfVariations:
    () =>
    ({ getState, setState }, { customer }) => {
      const { generationTool } = getState();

      const numberOfVariations = getDocumentDefaultNumberOfVariations(
        generationTool,
        !customerCanChangeNumberOfVariations(customer)
      );
      setState({ numberOfVariations });
    },

  recoverFromCreationError:
    (error) =>
    ({ setState }) => {
      setState({
        errorBriefGeneral: parseErrors(error).join('\n'),
        errorBriefGeneralCode: error.code || null,
      });
    },

  clearBriefUrlErrors:
    () =>
    ({ setState }) => {
      setState({
        errorBriefUrl: null,
        errorBriefGeneral: null,
        errorBriefGeneralCode: null,
      });
    },

  initBriefExtraction:
    () =>
    ({ getState, setState }) => {
      const { briefContent, extractedBriefs } = getState();

      const currentUrls = extractUrls(briefContent?.body, MAX_RESOURCE_PER_TYPE)?.urls ?? [];
      const resolvedUrls = Array.from(Object.keys(extractedBriefs));
      const allResolved = currentUrls.every((url) => resolvedUrls.includes(url));
      if (currentUrls.length === 0) {
        setState({ isExtractingBrief: false });
      } else if (!allResolved) {
        setState({ isExtractingBrief: true });
      }

      setState({ currentUrls });
    },

  updateExtractedBriefErrorsForUrl:
    (url, error) =>
    ({ setState, getState }) => {
      const { extractedBriefErrors } = getState();
      extractedBriefErrors[url] = {
        errorBriefGeneral: parseErrors(error).join('\n'),
        errorBriefGeneralCode: error.code || null,
      };
      setState({ extractedBriefErrors });
    },

  mergeIntoAttachedResourcesMap:
    (promptId, resources) =>
    ({ getState, setState }) => {
      const { attachedResourcesMap } = getState();
      setState({ attachedResourcesMap: { ...attachedResourcesMap, [promptId]: resources } });
    },

  requestRewriteInputSections:
    () =>
    async ({ getState, setState, dispatch }) => {
      const { rewriteContentType } = getState();

      setState({ loadingRewriteInputSections: true });
      try {
        const sections = await queryLongFormToolsForContentType(rewriteContentType);
        dispatch(PRIVATE_ACTIONS.initRewriteInputSections(sections));
      } finally {
        setState({ loadingRewriteInputSections: false });
      }
    },

  initRewriteInputSections:
    (sections) =>
    ({ getState, setState, dispatch }) => {
      const { rewriteContentType } = getState();
      const mapper = createRewriteInputSectionFromBackendMapper(rewriteContentType);
      const rewriteInputSections = sections.map(mapper);

      setState({ rewriteInputSections });
      dispatch(PRIVATE_ACTIONS.alignRewriteOutputSections());
    },

  alignRewriteOutputSections: () => alignRewriteOutputSectionsDebounced,
};

/**
 * Publicly available actions
 */
export const ACTIONS = {
  initGenerationTool:
    (generationTool, subTemplateId) =>
    ({ getState, setState, dispatch }) => {
      setState({
        generationTool,
        subTemplateId,
        freestyleToolContent: EMPTY_FREESTYLE_CONTENT,
        attachedResourcesMap: {},
        showAdvancedOptions: false, // close "Advanced" section each time user changes the tool
      });

      dispatch(ACTIONS.initDataDrivenTemplates());
      dispatch(ACTIONS.initSubGenerationToolsProps());
      dispatch(ACTIONS.clearBriefError());
      dispatch(ACTIONS.clearRewriteError());
      dispatch(ACTIONS.clearBriefExtractionErrors());
      dispatch(ACTIONS.initSubTemplateProperties());

      if (isMultiPromptTool(generationTool)) {
        setState({ isImageGenerationEnabled: true });
      }

      if (
        doesToolSupportCampaignKeywords(generationTool) &&
        !isRewriteGenerationTool(generationTool)
      ) {
        dispatch(ACTIONS.clearRegularAnnotations());
      } else {
        dispatch(ACTIONS.clearAnnotations());
      }
      dispatch(ACTIONS.clearAnnotationsError());
      dispatch(ACTIONS.clearStructureInstructionsData());
      dispatch(PRIVATE_ACTIONS.alignNumberOfVariations());
      dispatch(ACTIONS.initMultiPromptFields());
      dispatch(ACTIONS.setToolPrompts(null));
      dispatch(ACTIONS.setMultiPromptTemplateAdvancedSettings(null));
      dispatch(ACTIONS.setMultiPromptTemplateAdvancedSettingsDraft(null));

      // restore from cache if exists
      dispatch(ACTIONS.restoreGenerationToolOptionsCache());

      // This key should be used within uncontrolled components with internal state, like <PromptInstructions key={templateDataVersion} ..... />
      // Using this key to insure component renders with correct props after we are loading/reset/updating state
      dispatch(ACTIONS.incrementTemplateDataVersion());

      const { initialFreestyleToolContent } = getState();
      if (initialFreestyleToolContent) {
        dispatch(ACTIONS.initFreestyleToolData(initialFreestyleToolContent));
        dispatch(ACTIONS.resetInitialFreestyleToolContent());
      }
    },

  initDataDrivenTemplates:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();

      const availableTemplates = getToolGenerationModes(generationTool);
      const visibleTemplates = filterVisibleDDTemplates(availableTemplates);

      setState({
        availableDDTemplates: availableTemplates,
        visibleDDTemplates: visibleTemplates,
        selectedModeId: visibleTemplates[0],
        selectedModeType: GENERATION_MODE_TYPE.common,
        isCustomFormulasVisible: availableTemplates.includes(AI_SUGGESTION_MODE.customFormula),
      });
    },

  getGenerationTool:
    () =>
    ({ getState }) => {
      const { generationTool, generationToolOverride } = getState();

      if (generationTool === GENERATION_TOOL.freestyle && generationToolOverride) {
        return generationToolOverride;
      }

      return generationTool;
    },

  setGenerationToolOverride:
    (generationToolOverride) =>
    ({ setState }) => {
      setState({ generationToolOverride });
    },

  clearGenerationToolOverride:
    () =>
    ({ setState }) => {
      setState({ generationToolOverride: null });
    },

  getRewriteGenerationTool:
    () =>
    ({ getState }) => {
      const { rewriteContentType } = getState();
      return getShortFormContentTypeTool(rewriteContentType);
    },

  getFilteredSubGenerationToolsProps:
    () =>
    ({ getState }) => {
      const {
        generationTool: stateGenerationTool,
        generationToolOverride,
        subGenerationToolsProps,
      } = getState();
      const generationTool = generationToolOverride ?? stateGenerationTool;

      // If not a mixture tool, return null
      if (!isMixtureTool(generationTool)) {
        return null;
      }

      // If no override, filter existing state props
      if (!generationToolOverride) {
        const filteredSubGenerationToolsProps = {};
        Object.entries(subGenerationToolsProps).forEach(([key, value]) => {
          filteredSubGenerationToolsProps[key] = {
            suggestionMode: getAlignedSuggestionMode(value.selectedModeId, value.selectedModeType),
            structureInstructionsData: value.structureInstructionsData,
          };
          if (
            getAlignedSuggestionMode(value.selectedModeId, value.selectedModeType) ===
            AI_SUGGESTION_MODE.customFormula
          ) {
            filteredSubGenerationToolsProps[key].customFormulaId = value.selectedModeId;
          }
        });
        return filteredSubGenerationToolsProps;
      }

      // Build props from scratch for the overridden tool
      const subTools = getToolMixtureSubGenerationTools(generationTool);
      const modeOverrides = getToolMixtureSubGenerationToolsModeOverride(generationTool);
      const result = {};

      subTools.forEach((subTool) => {
        const visibleTemplates = filterVisibleDDTemplates(getToolGenerationModes(subTool));
        const selectedModeId = modeOverrides?.[subTool] ?? visibleTemplates[0];
        const suggestionMode = getAlignedSuggestionMode(selectedModeId, null);

        result[subTool] = {
          suggestionMode,
          structureInstructionsData: null,
        };

        if (suggestionMode === AI_SUGGESTION_MODE.customFormula) {
          result[subTool].customFormulaId = selectedModeId;
        }
      });

      return result;
    },

  getAssetType:
    () =>
    ({ getState }) => {
      const { generationTool } = getState();
      return getToolDefaultAssetType(generationTool);
    },

  getBriefContent:
    () =>
    ({ getState }) => {
      return getState().briefContent;
    },

  setBriefContent:
    (briefContent) =>
    ({ setState, dispatch }) => {
      setState({ briefContent });

      dispatch(PRIVATE_ACTIONS.initBriefExtraction());
      dispatch(PRIVATE_ACTIONS.storeBriefContentInCache());
    },

  getRewriteContentType:
    () =>
    ({ getState }) => {
      return getState().rewriteContentType;
    },
  updateRewriteContentType:
    (value) =>
    ({ setState, dispatch }) => {
      setState({
        rewriteContentType: value,
        rewriteInputMode: contentInputModeFromContentType(value),
      });
      dispatch(setError('rewrite', null));

      dispatch(PRIVATE_ACTIONS.requestRewriteInputSections());
      dispatch(ACTIONS.clearRewriteImproveMode());
      dispatch(ACTIONS.clearRewriteInstructions());
    },

  setRewriteInputMode:
    (value) =>
    ({ setState }) => {
      setState({ rewriteInputMode: value });
    },

  setUploadingResource:
    (value) =>
    ({ setState }) => {
      setState({ uploadingResource: value });
    },

  extractAssetsFromResource:
    (resourceId) =>
    async ({ getState, setState, dispatch }, { customer }) => {
      const { rewriteContentType } = getState();

      setState({ extractingResourceAssets: true });
      try {
        // request backend to extract content sections from the image
        const job = await extractAssetsFromResource({
          customerId: customer?.id,
          contentType: rewriteContentType,
          resourceId,
        });

        // wait for the extraction job to complete
        const result = await waitForJob(job.id);

        // process extracted sections and use them
        const preparedSections = prepareExtractedFromAssetSections(result.sectionsData);
        dispatch(PRIVATE_ACTIONS.initRewriteInputSections(preparedSections));

        // switch to the "content form" mode after extracting sections from image
        dispatch(ACTIONS.setRewriteInputMode(IMPROVER_CONTENT_INPUT_MODE.sections));
      } finally {
        setState({ extractingResourceAssets: false });
      }
    },

  getRewriteText:
    () =>
    ({ getState }) => {
      const { generationTool, briefContent, rewriteText } = getState();

      if (
        isBasicGenerationTool(generationTool) &&
        generationTool !== GENERATION_TOOL.basicContentImprover
      ) {
        return getRecomposeTextFromBrief(generationTool, briefContent);
      }

      return rewriteText;
    },

  updateRewriteText:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ rewriteText: value });

      dispatch(setError('rewrite', null));
    },

  getRewriteOutputSections:
    () =>
    ({ getState }) => {
      return getState().rewriteOutputSections;
    },

  getRewriteInputSectionByTool:
    (tool) =>
    ({ getState }) => {
      const { rewriteInputSections } = getState();
      return rewriteInputSections.find((section) => section.generationTool === tool);
    },

  updateRewriteInputSectionData:
    (sectionId, newData) =>
    ({ getState, setState, dispatch }) => {
      const { rewriteInputSections } = getState();

      const newValue = produce(rewriteInputSections, (draft) => {
        const index = draft.findIndex((section) => section.id === sectionId);
        if (index !== -1) {
          draft[index] = {
            ...draft[index],
            ...newData,
          };
        }
      });

      setState({ rewriteInputSections: newValue });
      dispatch(PRIVATE_ACTIONS.alignRewriteOutputSections());

      dispatch(setError('rewrite', null));
    },

  markRewriteSectionProcessing:
    (sectionId, processing) =>
    ({ getState, setState }) => {
      const newValue = produce(getState().rewriteProcessingSectionsIds, (draft) => {
        if (processing) {
          draft.add(sectionId);
        } else {
          draft.delete(sectionId);
        }
      });
      setState({ rewriteProcessingSectionsIds: newValue });
    },

  mergeRewriteSectionsFromAdvanced:
    (advancedSections) =>
    ({ getState, setState, dispatch }) => {
      const { rewriteContentType, rewriteInputSections, rewriteInputToOutputSectionsMap } =
        getState();

      const mergedInputSections = rewriteInputSections.map((inputSection) => {
        if (!isRewriteSectionSupportHtml(rewriteContentType, inputSection)) {
          return advancedSections.find(({ id }) => id === inputSection.id);
        }

        const relevantSectionsIds = rewriteInputToOutputSectionsMap.get(inputSection.id);
        const relevantSections = advancedSections.filter(({ id }) =>
          relevantSectionsIds?.includes(id)
        );
        return {
          ...inputSection,
          content: mergeRewriteSectionsIntoHtml(relevantSections),
        };
      });

      setState({
        rewriteInputSections: compact(mergedInputSections), // filter out possible empty sections
        rewriteOutputSections: advancedSections,
      });

      dispatch(ACTIONS.incrementTemplateDataVersion());
    },

  getRewriteSectionsForImprovement:
    ({ defaultGenerationParams, defaultStructureInstructionsData }) =>
    ({ getState }) => {
      const sectionsFilter = (section) => {
        // do not send section to BE if user haven't selected any images for it
        if (isImageGenerationTool(section.generationTool)) {
          return section.imageResource?.id != null;
        }

        // do not send empty text sections to BE
        return trim(section.content) !== '';
      };

      const sectionsMapper = (section) => {
        const improveParams = section.improveParams || {};
        const generationParams = getGenerationImproveParams({
          projectType: PROJECT_TYPE.document,
          improveParams,
        });

        const result = {
          generationTool: section.generationTool,
          content: '', // required field on BE, real value will be added for "text" sections
          recomposeMode: generationParams.recomposeMode,
        };

        if (isImageGenerationTool(section.generationTool)) {
          result.imageResourceId = section.imageResource?.id;

          if (improveParams.improveMode !== GENERATION_IMPROVE_MODE.doNotImprove) {
            if (generationParams?.recomposeMode) {
              result.recomposeMode = generationParams.recomposeMode;
              result.manualInstructions = generationParams.instructions;
              result.imageStyleId = generationParams?.media?.imageStyleId;
            } else if (defaultGenerationParams?.recomposeMode) {
              result.recomposeMode = defaultGenerationParams.recomposeMode;
              result.manualInstructions = defaultGenerationParams.instructions;
              result.imageStyleId = defaultGenerationParams?.media?.imageStyleId;
            }
          }
        } else {
          result.content = section.content || '';

          if (improveParams.improveMode !== GENERATION_IMPROVE_MODE.doNotImprove) {
            result.structureInstructionsData = isStructureInstructionsPresent(
              section.structureInstructionsData
            )
              ? section.structureInstructionsData
              : defaultStructureInstructionsData;

            if (generationParams?.recomposeMode) {
              result.recomposeMode = generationParams.recomposeMode;
              result.manualInstructions = generationParams.instructions;
              result.tovInput = generationParams.tovInput;
              result.onlyImproveScore = shouldImproveScoresForMode(generationParams.improveMode);
            } else if (defaultGenerationParams?.recomposeMode) {
              // use default generation params if no specific improve params were provided for this section
              result.recomposeMode = defaultGenerationParams.recomposeMode;
              result.manualInstructions = defaultGenerationParams.instructions;
              result.tovInput = defaultGenerationParams.tovInput;
              result.onlyImproveScore = shouldImproveScoresForMode(
                defaultGenerationParams.improveMode
              );
            }
          }
        }

        return result;
      };

      const { rewriteOutputSections } = getState();
      return rewriteOutputSections.filter(sectionsFilter).map(sectionsMapper);
    },

  createMultipleAnnotations:
    (annotations) =>
    ({ getState, dispatch }) => {
      const { generationTool } = getState();

      const isCampaignKeyword = doesToolSupportCampaignKeywords(generationTool);

      annotations.forEach((annotation) => {
        // Add new annotation to the list
        const newAnnotation = {
          id: annotation.id,
          type: isCampaignKeyword
            ? GENERATION_GUIDANCE_TYPE.campaignKeyword
            : GENERATION_GUIDANCE_TYPE.keyword,
          content: annotation,
        };
        dispatch(ACTIONS.addAnnotation(newAnnotation));
      });
    },

  getIsExtractingBrief:
    () =>
    ({ getState }) => {
      return getState().isExtractingBrief;
    },

  validateFreestyleContent:
    () =>
    ({ getState, dispatch }) => {
      // We don't need this validation in Infinity "prompt" mode
      if (dispatch(ACTIONS.inExtensionPromptMode())) {
        return true;
      }

      if (dispatch(ACTIONS.isSubTemplateSelected())) {
        return true;
      }

      const { freestyleToolContent } = getState();
      const instructions = { allInstructions: freestyleToolContent.allInstructions };

      const errors = validateFreestyleBrief(instructions);
      if (errors.allInstructions) {
        dispatch(setError('freestyleToolContent', errors));
        return false;
      }

      dispatch(setError('freestyleToolContent', null));
      return true;
    },

  validateBrief:
    (options = null) =>
    ({ getState, setState, dispatch }) => {
      const { generationTool, briefContent } = getState();

      const muteErrors = options?.silent;
      const highlightFields = options?.highlight;

      // Brief is optional for rewrite tools and Infinity "prompt" mode
      const isRewriteTool = isRewriteGenerationTool(generationTool);
      if (
        isRewriteTool ||
        generationTool === GENERATION_TOOL.basicContentImprover ||
        dispatch(ACTIONS.inExtensionPromptMode()) ||
        isMultiPromptTool(generationTool)
      ) {
        return true;
      }

      if (dispatch(ACTIONS.isSubTemplateSelected())) {
        return true;
      }

      const briefType = getToolBriefType(generationTool);
      const validation = validateDocumentBrief(briefContent, {
        briefType,
        generationTool,
        skipEmailPromotionField: options?.silent,
      });
      if (!validation.valid) {
        if (!muteErrors) {
          setState({ briefError: omit(validation, ['valid']) });
        }
        return false;
      } else if (highlightFields) {
        setState({ briefError: { highlight: true } });
        return false;
      }

      setState({ briefError: null });
      return true;
    },

  validateParams:
    () =>
    ({ getState, dispatch }) => {
      const { generationTool, callToAction } = getState();

      // If "freestyle" tool selected - run separate validation
      if (isFreestyleGenerationTool(generationTool)) {
        return dispatch(ACTIONS.validateFreestyleContent());
      }

      // Validate "brief" and other generation params
      const briefValid = doesToolSupportPromptInstruction(generationTool)
        ? dispatch(ACTIONS.validateFreestyleContent())
        : dispatch(ACTIONS.validateBrief());

      const shouldRewrite = dispatch(ACTIONS.isShouldRewrite());
      const rewriteValid = shouldRewrite ? dispatch(PRIVATE_ACTIONS.validateRewrite()) : true;

      const campaignKeywordsValid = dispatch(ACTIONS.validateCampaignKeywords());
      const callToActionTextValid = validateCallToActionText(callToAction);
      const subTemplatePropertiesValid = dispatch(ACTIONS.validateSubTemplateProperties());

      return (
        briefValid &&
        rewriteValid &&
        campaignKeywordsValid &&
        callToActionTextValid.valid &&
        subTemplatePropertiesValid
      );
    },

  clearBriefError:
    () =>
    ({ setState, dispatch }) => {
      setState({ briefError: {} });
      dispatch(setError('freestyleToolContent'), null);
    },

  clearRewriteError:
    () =>
    ({ dispatch }) => {
      dispatch(setError('rewrite', null));
    },

  clearBriefExtractionErrors:
    () =>
    ({ setState }) => {
      setState({
        extractedBriefErrors: {},
      });
    },

  selectTemplate:
    (id, type) =>
    ({ getState, setState, dispatch }) => {
      const { generationTool } = getState();

      if (doesToolSupportsPersonaForMode({ generationTool, suggestionMode: id })) {
        dispatch(PRIVATE_ACTIONS.restoreCustomerPersonaFromCache());
      } else {
        dispatch(PRIVATE_ACTIONS.storeCustomerPersonaInCache());
      }
      setState({
        selectedModeId: id,
        selectedModeType: type,
      });
    },

  selectSubGenerationToolTemplate:
    (id, type, subGenerationTool) =>
    ({ getState, setState }) => {
      const { subGenerationToolsProps: currentProps } = getState();
      if (!currentProps[subGenerationTool]) {
        return;
      }

      const newProps = produce(currentProps, (draft) => {
        draft[subGenerationTool].selectedModeId = id;
        draft[subGenerationTool].selectedModeType = type;
      });
      setState({ subGenerationToolsProps: newProps });
    },

  clearTemplate:
    () =>
    ({ setState }) => {
      setState({
        selectedModeId: null,
        selectedModeType: null,
      });
    },

  getSelectedModeId:
    () =>
    ({ getState }) => {
      return getState().selectedModeId;
    },
  getSelectedModeType:
    () =>
    ({ getState }) => {
      return getState().selectedModeType;
    },

  getGenerationModeId:
    () =>
    ({ getState, dispatch }) => {
      const {
        generationTool: stateGenerationTool,
        generationToolOverride,
        selectedModeId,
        language,
      } = getState();
      const generationTool = generationToolOverride ?? stateGenerationTool;

      // When using override, always return the default mode for the overridden tool
      if (generationToolOverride) {
        return getToolDefaultGenerationMode(generationTool);
      }

      if (
        isNonEnglish(language) ||
        (!dispatch(ACTIONS.isShouldRewrite()) &&
          isBasicGenerationTool(generationTool) &&
          !doesToolHasMultipleGenerationModes(generationTool))
      ) {
        return getToolDefaultGenerationMode(generationTool);
      }

      return selectedModeId;
    },
  getGenerationModeType:
    () =>
    ({ getState }) => {
      const {
        generationTool: stateGenerationTool,
        generationToolOverride,
        selectedModeType,
        language,
      } = getState();
      const generationTool = generationToolOverride ?? stateGenerationTool;

      // When using override, always return common mode type
      if (generationToolOverride) {
        return null;
      }

      if (isBasicGenerationTool(generationTool) || isNonEnglish(language)) {
        return GENERATION_MODE_TYPE.common;
      }
      return selectedModeType;
    },

  isShouldRewrite:
    () =>
    ({ getState }) => {
      const { generationTool, selectedModeId } = getState();

      if (isRecomposeMode(selectedModeId)) {
        return true;
      }
      return isRewriteGenerationTool(generationTool);
    },

  getSelectedPersona:
    () =>
    ({ getState }) => {
      return getState().selectedCustomerPersona;
    },

  getPersonaForGeneration:
    () =>
    ({ getState }) => {
      const { generationTool, selectedModeId, selectedCustomerPersona } = getState();

      if (
        !doesToolSupportsPersonaForMode({
          generationTool,
          suggestionMode: selectedModeId,
        })
      ) {
        return null;
      }

      return selectedCustomerPersona;
    },

  selectProjectPersona:
    (persona) =>
    async ({ setState }, { project, isApplicationModeInfinity }) => {
      // We do not care about "project.persona" selection persistence in Infinity
      if (isApplicationModeInfinity) {
        setState({ selectedCustomerPersona: persona });
        return;
      }

      setState({ isUpdatingProjectPersonas: true });
      try {
        if (persona) {
          const newValue = persona ? [persona.id] : [];
          await updateProjectPersonas(newValue, project.id);
        }

        setState({ selectedCustomerPersona: persona });
      } finally {
        setState({ isUpdatingProjectPersonas: false });
      }
    },

  validateForGeneration:
    () =>
    ({ getState }) => {
      const { selectedModeType, selectedModeId, generationTool } = getState();
      // We might need more validations here but for now we just check if we have selected mode if and type
      return (
        isMixtureTool(generationTool) ||
        isMultiPromptTool(generationTool) ||
        (selectedModeId && selectedModeType)
      ); //TODO: add validation for sub generation tools
    },

  updateNewAnnotationText:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ newAnnotationText: value });

      // Validate new annotation content
      const wordsValidation = dispatch(PRIVATE_ACTIONS.validateAnnotationText(value));
      if (!wordsValidation.valid) {
        dispatch(setError('annotations', wordsValidation.message));
        return;
      }

      dispatch(ACTIONS.clearAnnotationsError());
    },

  clearAnnotationsError:
    () =>
    ({ dispatch }) => {
      dispatch(setError('annotations', null));
      dispatch(setError('campaignKeywords', null));
      dispatch(setError('annotationsCounter', null));
    },

  clearCallToActionError:
    () =>
    ({ dispatch }) => {
      dispatch(setError('callToAction', null));
    },

  updateNewCampaignKeywordsText:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ newCampaignKeywordsText: value });

      const wordsValidation = dispatch(
        PRIVATE_ACTIONS.validateAnnotationText(value, GENERATION_GUIDANCE_TYPE.campaignKeyword)
      );
      if (!wordsValidation.valid) {
        dispatch(setError('campaignKeywords', wordsValidation.message));
        return;
      }

      dispatch(setError('campaignKeywords', null));
    },

  validateCampaignKeywords:
    (options = null) =>
    ({ dispatch, getState }) => {
      const { generationTool } = getState();
      const muteErrors = options?.silent;

      // Return early if "campaign keywords" are not supported for the current tool, or is SEOMetadata which the keywords are optional
      if (
        !doesToolSupportCampaignKeywords(generationTool) ||
        isLandingSeoMetaDataGenerationTool(generationTool)
      ) {
        return true;
      }

      const campaignKeywords = dispatch(PRIVATE_ACTIONS.getCampaignKeywordsAnnotations());
      if (campaignKeywords.length === 0) {
        if (!muteErrors) {
          dispatch(setError('campaignKeywords', 'Input needed'));
        }
        return false;
      }

      return true;
    },

  createNewAnnotation:
    ({ content, type, score, origin, id }, options = null) =>
    ({ getState, dispatch }, { notifications }) => {
      const trimmedText = content?.trim();
      const toastValidation = options?.displayValidations === 'toast';
      const isCampaignKeyword = type === GENERATION_GUIDANCE_TYPE.campaignKeyword;

      // Validate and display errors
      const validation = dispatch(PRIVATE_ACTIONS.validateNewAnnotation(trimmedText, type));
      if (!validation.valid) {
        if (validation.message) {
          if (toastValidation) {
            notifications.displayWarning(validation.message);
          } else {
            dispatch(
              setError(isCampaignKeyword ? 'campaignKeywords' : 'annotations', validation.message)
            );
            dispatch(setError('annotationsCounter', validation.message));
          }
        }

        return false;
      }

      // Add new annotation to the list
      const newAnnotation = {
        id,
        type: type || GENERATION_GUIDANCE_TYPE.keyword,
        content: trimmedText,
        score,
        origin: origin || USER_ANNOTATIONS_ORIGIN,
      };
      dispatch(ACTIONS.addAnnotation(newAnnotation));

      // Clear "annotations" input
      if (isCampaignKeyword) {
        dispatch(ACTIONS.updateNewCampaignKeywordsText(''));
      } else {
        dispatch(ACTIONS.updateNewAnnotationText(''));
      }
      return true;
    },

  completePendingAnnotation:
    (type) =>
    ({ getState, dispatch }) => {
      const { newAnnotationText, newCampaignKeywordsText } = getState();

      const text =
        type === GENERATION_GUIDANCE_TYPE.campaignKeyword
          ? newCampaignKeywordsText
          : newAnnotationText;
      const trimmedText = text.trim();

      // Do not add empty annotations
      if (trimmedText.length === 0) {
        return true;
      }

      return dispatch(
        ACTIONS.createNewAnnotation({
          content: trimmedText,
          type,
        })
      );
    },

  addAnnotation:
    (annotation) =>
    ({ getState, dispatch }) => {
      const { annotations } = getState();
      const newList = [...annotations, annotation];
      dispatch(ACTIONS.setAnnotations(newList));
    },

  clearAnnotations:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.setAnnotations([]));
    },

  getAnnotationByContent:
    (content) =>
    ({ getState }) => {
      const { annotations } = getState();
      return annotations.find((annotation) => annotation.content === content);
    },

  clearTalkingPoints:
    () =>
    ({ getState, setState }) => {
      const { annotations } = getState();
      const nonTalkingPoints = isNotTalkingPointAnnotation(annotations);
      setState({ annotations: nonTalkingPoints });
    },

  clearCampaignKeywordsAnnotations:
    () =>
    ({ getState, setState }) => {
      const { annotations } = getState();
      const nonCampaignKeywordsAnnotations = annotations.filter(
        (annotation) => annotation.type !== GENERATION_GUIDANCE_TYPE.campaignKeyword
      );
      setState({ annotations: nonCampaignKeywordsAnnotations });
    },

  clearRegularAnnotations:
    () =>
    ({ getState, setState }) => {
      const { annotations } = getState();
      const specialAnnotations = annotations.filter(
        (annotation) =>
          annotation.type === GENERATION_GUIDANCE_TYPE.campaignKeyword ||
          annotation.type === GENERATION_GUIDANCE_TYPE.hashtag
      );
      setState({ annotations: specialAnnotations });
    },

  setAnnotations:
    (newAnnotations) =>
    ({ setState }) => {
      setState({ annotations: [...newAnnotations] });
    },

  getAnnotationsForGeneration:
    () =>
    ({ getState, dispatch }) => {
      const { generationTool, rewriteContentType, selectedModeId, annotations } = getState();

      const alignedTool = isBasicContentImproverTool(generationTool)
        ? getShortFormContentTypeTool(rewriteContentType)
        : generationTool;
      const result = [];

      const isInfinityPromptModeAndGoogleRSA =
        dispatch(ACTIONS.inExtensionPromptMode()) && alignedTool === GENERATION_TOOL.googleRSA;

      const toolSupportsSuggestKeywords = doesToolSupportSuggestKeywordsForMode({
        generationTool: alignedTool,
        suggestionMode: selectedModeId,
      });
      if (toolSupportsSuggestKeywords || isInfinityPromptModeAndGoogleRSA) {
        const suggestKeywords = annotations.filter(
          (annotation) =>
            annotation.type !== GENERATION_GUIDANCE_TYPE.campaignKeyword &&
            annotation.type !== GENERATION_GUIDANCE_TYPE.hashtag
        );
        result.push(...suggestKeywords);
      }

      const toolSupportsCampaignKeywords = doesToolSupportCampaignKeywords(alignedTool);
      if (toolSupportsCampaignKeywords) {
        const campaignKeywords = annotations.filter(
          (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.campaignKeyword
        );
        result.push(...campaignKeywords);
      }

      return result;
    },

  hasRegularKeywordsAnnotations:
    () =>
    ({ dispatch }) => {
      const annotations = dispatch(PRIVATE_ACTIONS.getRegularAnnotations());
      return annotations.length > 0;
    },
  removeAnnotation:
    (annotationId) =>
    ({ getState, setState, dispatch }) => {
      const { annotations, newAnnotationText } = getState();
      const newList = annotations.filter(({ id }) => id !== annotationId);
      dispatch(ACTIONS.setAnnotations(newList));

      setState({ newAnnotationText: Object.assign('', newAnnotationText) });
      dispatch(ACTIONS.updateNewAnnotationText(newAnnotationText)); //revalidate error
    },

  initSubGenerationToolsProps:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();
      if (!isMixtureTool(generationTool)) {
        return;
      }
      const subGenerationTools = getToolMixtureSubGenerationTools(generationTool);
      const subGenerationToolsProps = {};
      subGenerationTools.forEach((subGenerationTool) => {
        const availableTemplates = getToolGenerationModes(subGenerationTool);
        const visibleTemplates = filterVisibleDDTemplates(availableTemplates);
        const subGenerationToolsModeOverride =
          getToolMixtureSubGenerationToolsModeOverride(generationTool);
        const selectedModeId = subGenerationToolsModeOverride?.[subGenerationTool]
          ? subGenerationToolsModeOverride?.[subGenerationTool]
          : visibleTemplates[0];
        const selectedModeType = GENERATION_MODE_TYPE.common;
        subGenerationToolsProps[subGenerationTool] = {
          selectedModeId,
          selectedModeType,
          structureInstructionsData: null,
          isCustomFormulasVisible: availableTemplates.includes(AI_SUGGESTION_MODE.customFormula),
          visibleDDTemplates: visibleTemplates,
          errors: {},
        };
      });
      setState({ subGenerationToolsProps });
    },

  getCampaignKeywordsAnnotations: PRIVATE_ACTIONS.getCampaignKeywordsAnnotations,

  initFreestyleToolData:
    (toolData, subTemplate) =>
    ({ setState, dispatch }, { resources }) => {
      if (!toolData) {
        return;
      }

      if (dispatch(ACTIONS.isSubTemplateSelected())) {
        if (!subTemplate) return;

        const question = subTemplate.questions.at(-1);
        if (!question) return;

        dispatch(
          ACTIONS.setSubTemplateProperties({
            content: toolData.allInstructions,
            allInstructionsData: toolData.allInstructionsData,
            question: {
              text: question.question,
              mandatory: question.mandatory,
              placeholder: question.placeholder,
            },
            subTemplateId: subTemplate.id,
          })
        );
        return;
      }

      setState({ freestyleToolContent: { ...toolData } });

      // Check if incoming data has any attached resources
      const allInstructionsData = toolData?.allInstructionsData || {};
      const dataResourceIds = Object.values(allInstructionsData.placeholders || {}).map(
        (placeholder) => placeholder.id
      );

      // Attach them to the "brief"
      const dataResources = resources.filter((resource) => dataResourceIds.includes(resource.id));
      dispatch(PRIVATE_ACTIONS.mergeIntoAttachedResourcesMap(SINGLE_PROMPT_ID, dataResources));

      dispatch(ACTIONS.updateGenerationToolOptionsCache());
    },

  setFreestyleToolContent:
    (newContent, allInstructionsData, id, isSuggested) =>
    ({ getState, setState, dispatch }) => {
      const { freestyleToolContent, preSuggestedPromptContent } = getState();

      if (freestyleToolContent.allInstructions === newContent) {
        return;
      }

      let personalizedPrompt =
        allInstructionsData.personalizedPrompt || freestyleToolContent.personalizedPrompt;
      let improvedPrompt =
        allInstructionsData.improvedPrompt || freestyleToolContent.improvedPrompt;

      // if the content is not suggested and is empty or the same as the pre-suggested content, we should remove personalized or improved marking
      if (
        !isSuggested &&
        (!convertHtmlContentToPlainText(newContent).trim() ||
          preSuggestedPromptContent.trim() === newContent.trim())
      ) {
        personalizedPrompt = false;
        improvedPrompt = false;
      }

      setState({
        freestyleToolContent: {
          ...freestyleToolContent,
          id,
          allInstructions: newContent,
          allInstructionsData,
          personalizedPrompt,
          improvedPrompt,
        },
      });

      dispatch(setError('freestyleToolContent'), null);
      dispatch(ACTIONS.resetQuestionsPopover());
    },
  getFreestyleToolContent:
    () =>
    ({ getState }) => {
      return getState().freestyleToolContent;
    },

  resetFreestyleToolContent:
    () =>
    ({ setState }) => {
      setState({
        freestyleToolContent: EMPTY_FREESTYLE_CONTENT,
      });
    },

  openFreestyleToolSavePopup:
    () =>
    ({ setState }) => {
      setState({
        isFreestyleToolSaveViewVisible: true,
      });
    },

  closeFreestylePopupViews:
    () =>
    ({ setState }) => {
      setState({
        isFreestyleToolSaveViewVisible: false,
        isFreestyleToolConfirmChangesViewVisible: false,
      });
    },

  setNumberOfVariations:
    (numberOfVariations) =>
    ({ setState }) => {
      setState({ numberOfVariations });
    },

  getNumberOfVariations:
    () =>
    ({ getState }) => {
      return getState().numberOfVariations;
    },

  getLanguage:
    () =>
    ({ getState }) => {
      return getState().language;
    },

  setLanguage:
    (language) =>
    ({ getState, setState, dispatch }) => {
      setState({ languageSource: LANGUAGE_SOURCE.user });

      if (getState().language === language) {
        return;
      }
      setState({ language });

      dispatch(ACTIONS.clearAnnotations());
      dispatch(ACTIONS.clearStructureInstructionsData());

      // Save selected language to local storage
      try {
        ls.documentGenerationLanguage.write(language);
      } catch (error) {
        captureMessage("Couldn't persist generation language", {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'CanvasProject/LocalStore.setLanguage',
          },
        });
      }
    },

  getLanguageForGeneration:
    () =>
    ({ getState }) => {
      const { language } = getState();
      return normalizeLanguage(language);
    },

  getLanguageTargetFormality:
    () =>
    ({ getState }) => {
      const { language } = getState();
      return getLanguageTargetFormality(language);
    },

  setGenerating:
    (value) =>
    ({ setState }) => {
      setState({ generating: value });
    },

  setDisabledGeneration:
    (value) =>
    ({ setState }) => {
      setState({ disabledGeneration: value });
    },

  inExtensionPromptMode:
    () =>
    ({ getState }, { isApplicationModeInfinity }) => {
      const { infinityExtensionMode } = getState();
      return (
        isApplicationModeInfinity &&
        infinityExtensionMode === INFINITY_EXTENSION_MODE.promptGeneration
      );
    },

  getRewriteInstructions:
    () =>
    ({ getState }) =>
      getState().rewriteInstructions,

  setRewriteInstructions:
    (newValue) =>
    ({ setState, dispatch }) => {
      setState({ rewriteInstructions: newValue || '' });
      dispatch(setError('rewriteManualInstructions', null));
    },

  clearRewriteInstructions:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.setRewriteInstructions(''));
    },

  setRewriteImproveMode:
    (newValue) =>
    ({ setState, getState, dispatch }) => {
      const { rewriteImproveMode: currentValue } = getState();
      setState({ rewriteImproveMode: newValue });

      // clear SFV value when switching to/from SFV mode
      if (
        (newValue === GENERATION_IMPROVE_MODE.sfv ||
          currentValue === GENERATION_IMPROVE_MODE.sfv) &&
        newValue !== currentValue
      ) {
        dispatch(ACTIONS.clearStructureInstructionsData());
      }

      // clear "manual instructions" when switching to/from this mode
      if (
        (newValue === GENERATION_IMPROVE_MODE.instructions ||
          currentValue === GENERATION_IMPROVE_MODE.instructions) &&
        newValue !== currentValue
      ) {
        dispatch(ACTIONS.clearRewriteInstructions());
      }
    },

  getRewriteImproveMode:
    () =>
    ({ getState }) =>
      getState().rewriteImproveMode,

  clearRewriteImproveMode:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.setRewriteImproveMode(null));
    },

  hasBriefErrors:
    () =>
    ({ getState }) => {
      const { errorBriefGeneral, errorBriefGeneralCode } = getState();
      return !!errorBriefGeneral || !!errorBriefGeneralCode;
    },

  validateBriefUrl:
    (generationTool) =>
    ({ getState, setState, dispatch }) => {
      const { briefUrl } = getState();

      // Validate only "URL" first, if it's not valid, no use in validating other fields
      const validationUrl = validateUrl(briefUrl, { generationTool });
      if (!validationUrl.valid) {
        setState({ errorBriefUrl: validationUrl.messages.join() });
        return false;
      }

      // Clear errors
      setState({
        errorBriefUrl: null,
      });
      dispatch(PRIVATE_ACTIONS.clearBriefUrlErrors());

      return true;
    },

  shouldExtractUrlBrief:
    () =>
    ({ getState, setState, dispatch }) => {
      const {
        generationTool,
        briefContent,
        extractedBriefIds,
        freestyleToolContent,
        multiPromptFields,
        toolPrompts,
        multiPromptActiveStep,
      } = getState();

      if (!doesToolSupportBackgroundUrlBriefExtraction(generationTool)) {
        return;
      }

      const content =
        isFreestyleGenerationTool(generationTool) ||
        doesToolSupportPromptInstruction(generationTool)
          ? freestyleToolContent?.allInstructions
          : isMultiPromptTool(generationTool)
            ? multiPromptActiveStep === MULTI_PROMPT_TEMPLATE_STEPS[0]
              ? serializeMultiPromptFields(multiPromptFields)
              : serializeToolPrompts(toolPrompts)
            : briefContent?.body;
      const supportedCount = isMultiPromptTool(generationTool) ? 30 : MAX_RESOURCE_PER_TYPE;

      const urls = extractUrls(content, supportedCount)?.urls ?? [];
      setState({ currentUrls: urls });
      if (urls.length === 0) {
        // no urls detected
        return;
      }

      const urlsToExtract = [];
      // Send only new URLs to extraction
      urls.forEach((url) => {
        if (!extractedBriefIds[url]) {
          urlsToExtract.push(url);
        }
      });
      if (urlsToExtract.length === 0) {
        setState({ isExtractingBrief: false });
        return;
      }
      if (doesToolSupportPromptInstruction(generationTool)) {
        setState({ isExtractingBrief: true });
      }
      dispatch(ACTIONS.extractBrief(urlsToExtract));
    },

  extractBrief:
    (urlsToExtract) =>
    ({ getState, setState, dispatch }, { customer }) => {
      const { generationTool, selectedModeId, extractedBriefIds } = getState();

      // for url import, brief type should always be unifiedContent
      const briefType = BRIEF_TYPE.unifiedContent;
      urlsToExtract.forEach((url) => {
        extractBrief({
          customerId: customer?.id,
          url,
          briefType,
          generationTool: alignToolForGeneration(generationTool),
          suggestionMode: selectedModeId,
        })
          .then(({ id }) => {
            extractedBriefIds[url] = id;
            setState({ extractedBriefIds }); // And errors in the catch
            dispatch(ACTIONS.shouldWaitForBriefExtraction(urlsToExtract));
          })
          .catch((error) => {
            dispatch(PRIVATE_ACTIONS.updateExtractedBriefErrorsForUrl(url, error));
            dispatch(PRIVATE_ACTIONS.recoverFromCreationError(error)); // for backward compatibility, remove for multiple
            delete extractedBriefIds[url];
          });
      });
    },

  shouldWaitForBriefExtraction:
    (urls) =>
    ({ getState, setState, dispatch }) => {
      const { generationTool, briefContent } = getState();
      return new Promise((resolve) => {
        if (urls.length === 0) {
          resolve(null);
        }

        const { extractedBriefs, extractedBriefErrors } = getState();
        const resolvedUrls = {};

        const resolveIfReady = () => {
          if (areAllUrlsExtracted(urls, resolvedUrls)) {
            const successfulExtractions = getSuccessfulExtractions(resolvedUrls);
            const hasErrors = urls.length > Object.keys(successfulExtractions).length;
            // This assumes that Social only supports single URL. If reqs change - this should as well.
            let isBlocking = false;
            if (hasErrors) {
              isBlocking = isFreestyleGenerationTool(generationTool)
                ? Object.keys(successfulExtractions).length === 0
                : isEnoughContentForExtraction(briefContent?.body);
            }
            const failedBriefs = urls.filter((url) => Object.hasOwn(extractedBriefErrors, url));
            const { currentUrls } = getState();
            // Since we support only 1 url in social, check the first current url
            if (isEqual(sortBy(currentUrls), sortBy(urls))) {
              setState({ isExtractingBrief: false });
            }
            resolve({
              errors: hasErrors,
              blockingErrors: isBlocking,
              extractedBriefs: successfulExtractions,
              failedBriefs,
            });
          }
        };

        urls.forEach((url) => {
          const inProgressBriefId = getState().extractedBriefIds[url];
          if (extractedBriefs[url]) {
            // already fetched, resolve
            resolvedUrls[url] = extractedBriefs[url];
            resolveIfReady();
          } else if (inProgressBriefId) {
            waitForBriefExtraction(inProgressBriefId)
              .then((extractedBrief) => {
                const newBrief = mapExtractedContentForEditing(extractedBrief);
                const body = newBrief?.article || '';
                extractedBriefs[url] = body;
                setState({ extractedBriefs });
                resolvedUrls[url] = body;
              })
              .catch((error) => {
                resolvedUrls[url] = null;
                dispatch(PRIVATE_ACTIONS.updateExtractedBriefErrorsForUrl(url, error));
              })
              .finally(() => {
                resolveIfReady();
              });
          } else {
            resolveIfReady();
          }
        });
      });
    },

  getUrlForGeneration:
    () =>
    ({ getState }) => {
      return getState().currentUrls;
    },

  toggleShowAdvancedOptions:
    () =>
    ({ getState, setState }) => {
      const { showAdvancedOptions: currentValue } = getState();
      setState({ showAdvancedOptions: !currentValue });
    },

  updateGenerationToolOptionsCache:
    () =>
    ({ getState, setState }) => {
      const {
        generationToolOptionsCache: cache,
        generationTool,
        selectedModeId,
        selectedModeType,
        annotations,
        multiPromptFields,
        toolPrompts,
        chainId,
        csvChainId,
        customMultiPromptId,
        freestyleToolContent,
        subGenerationToolsProps,
        attachedResourcesMap,
        numberOfVariations,
        structureInstructionsData,
        subTemplateProperties,
        subTemplateId,
      } = getState();

      const cacheKey = toolCacheKey(generationTool, customMultiPromptId, subTemplateId);

      // For tools with sub templates, need to generate cache key only for selected template
      if (!cacheKey) {
        return;
      }

      setState({
        generationToolOptionsCache: {
          ...cache,
          [cacheKey]: {
            selectedModeId,
            selectedModeType,
            annotations,
            multiPromptFields,
            toolPrompts,
            chainId,
            csvChainId,
            customMultiPromptId,
            freestyleToolContent: cloneDeep(freestyleToolContent),
            subGenerationToolsProps,
            attachedResourcesMap,
            numberOfVariations,
            structureInstructionsData,
            // Saving sub_template_properties only for real sub templates, (-1) is prompt
            ...(subTemplateId !== PROMPT_SUB_TEMPLATE_ID ? { subTemplateProperties } : {}),
          },
        },
      });
    },

  restoreGenerationToolOptionsCache:
    () =>
    ({ getState, setState, dispatch }) => {
      const {
        generationToolOptionsCache: cache,
        generationTool,
        customMultiPromptId,
        subTemplateId,
      } = getState();
      const cacheKey = toolCacheKey(generationTool, customMultiPromptId, subTemplateId);
      if (!cache[cacheKey]) {
        return;
      }

      if (cache[cacheKey].selectedModeId) {
        setState({
          selectedModeId: cache[cacheKey].selectedModeId,
          selectedModeType: cache[cacheKey].selectedModeType,
        });
      }
      if (cache[cacheKey].freestyleToolContent) {
        setState({ freestyleToolContent: cache[cacheKey].freestyleToolContent });
      }
      if (cache[cacheKey].annotations) {
        const annotationsList = cache[cacheKey].annotations;
        dispatch(ACTIONS.setAnnotations(annotationsList));
      }
      if (cache[cacheKey].multiPromptFields) {
        dispatch(ACTIONS.setMultiPromptFields(cache[cacheKey].multiPromptFields));
      }
      if (cache[cacheKey].toolPrompts) {
        dispatch(ACTIONS.setToolPrompts(cache[cacheKey].toolPrompts));
      }
      if (cache[cacheKey].chainId) {
        dispatch(ACTIONS.setChainId(cache[cacheKey].chainId));
      }
      if (cache[cacheKey].csvChainId) {
        dispatch(ACTIONS.setCsvChainId(cache[cacheKey].csvChainId));
      }
      if (cache[cacheKey].customMultiPromptId) {
        dispatch(ACTIONS.setCustomMultiPromptId(cache[cacheKey].customMultiPromptId));
        if (cache[cacheKey].subGenerationToolsProps) {
          setState({ subGenerationToolsProps: cache[cacheKey].subGenerationToolsProps });
        }
      }
      if (cache[cacheKey].attachedResourcesMap) {
        setState({ attachedResourcesMap: cache[cacheKey].attachedResourcesMap });
      }
      if (cache[cacheKey].numberOfVariations) {
        setState({ numberOfVariations: cache[cacheKey].numberOfVariations });
      }
      if (cache[cacheKey].structureInstructionsData) {
        setState({ structureInstructionsData: cache[cacheKey].structureInstructionsData });
      }
      if (cache[cacheKey].subGenerationToolsProps) {
        setState({ subGenerationToolsProps: cache[cacheKey].subGenerationToolsProps });
      }
      if (cache[cacheKey].subTemplateProperties) {
        setState({ subTemplateProperties: cache[cacheKey].subTemplateProperties });
      }
    },

  getBriefType:
    () =>
    ({ getState }) =>
      getToolBriefType(getState().generationTool),

  hasBriefContent:
    () =>
    ({ getState, dispatch }) => {
      const { briefContent } = getState();
      const briefType = dispatch(ACTIONS.getBriefType());
      return checkBriefHasContent(briefType, briefContent);
    },

  suggestPrompt:
    (type, options) =>
    async ({ getState, setState, dispatch }, { project, notifications }) => {
      const { freestyleToolContent, generationTool, generationToolOptionsCache } = getState();
      setState({ isSuggestPromptLoading: true });
      let urlExtractions = [];
      try {
        // wait for brief extraction to finish
        let briefExtraction;
        if (options?.briefExtractionPromise) {
          briefExtraction = await options?.briefExtractionPromise;
          if (briefExtraction?.blockingErrors) {
            return { briefExtractionResult: briefExtraction };
          }

          const extractedBriefs = briefExtraction?.extractedBriefs;
          if (extractedBriefs && Object.keys(extractedBriefs).length > 0) {
            // This is legacy support, to avoid risk the url is used somewhere else in the system (tracking events, for e.g.)
            urlExtractions = Object.entries(extractedBriefs).map(([url, extractedBrief]) => ({
              url,
              extractedBrief,
            }));
          }
        }
        const currentContent = freestyleToolContent.allInstructions;
        const { suggestedPrompt, html, ratingCount } = await suggestPrompt({
          projectId: project.id,
          numberOfVariations: 1,
          freestyleToolAllInstructionsData: JSON.stringify(
            freestyleToolContent.allInstructionsData
          ),
          freestyleToolId: freestyleToolContent.id,
          generationTool: getState().generationTool,
          type,
          urlExtractions,
        });
        if (type === SUGGEST_PROMPT_TYPES.personalize && ratingCount !== null) {
          return;
        }

        // cached value must be updated as well
        const cache = { ...generationToolOptionsCache };
        if (cache?.[generationTool]?.freestyleToolContent?.allInstructionsData) {
          cache[generationTool].freestyleToolContent.allInstructionsData.html =
            replacePromptTags(html);
          setState({ generationToolOptionsCache: { ...cache } });
        }

        setState({ preSuggestedPromptContent: currentContent });

        dispatch(
          ACTIONS.setFreestyleToolContent(
            suggestedPrompt,
            {
              html,
              placeholders: {},
              serialized: suggestedPrompt,
              personalizedPrompt: type === SUGGEST_PROMPT_TYPES.personalize,
              improvedPrompt: type === SUGGEST_PROMPT_TYPES.improve,
            },
            null,
            true
          )
        );
        return { html };
      } catch (error) {
        //in case of error, go back to not using suggested prompt as none was created.
        notifications.displayError(error);
      } finally {
        setState({ isSuggestPromptLoading: false });
      }
    },

  generationToolRatings:
    () =>
    async ({ getState }, { project, notifications }) => {
      try {
        const generationTool = getState().generationTool;
        if (isBareboneProject(project)) {
          return {};
        }
        const data = await generationToolRatings({
          projectId: project.id,
          generationTool,
        });
        return JSON.parse(data);
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getMultiPromptActiveStep:
    () =>
    ({ getState }) => {
      return getState().multiPromptActiveStep;
    },

  incrementMultiPromptActiveStep:
    () =>
    ({ getState, setState }) => {
      const currentStep = getState().multiPromptActiveStep;
      const currentIndex = getStepIndex(currentStep);
      const isLastStep = currentIndex === MULTI_PROMPT_TEMPLATE_STEPS.length - 1;

      if (isLastStep || currentIndex < 0) {
        return;
      }

      const newActiveStep = MULTI_PROMPT_TEMPLATE_STEPS[currentIndex + 1];
      setState({ multiPromptActiveStep: newActiveStep });
    },

  decrementMultiPromptActiveStep:
    () =>
    ({ getState, setState }) => {
      const currentStep = getState().multiPromptActiveStep;
      const currentIndex = getStepIndex(currentStep);
      const isFirstStep = currentIndex === 0;

      if (isFirstStep || currentIndex < 0) {
        return;
      }

      const newActiveStep = MULTI_PROMPT_TEMPLATE_STEPS[currentIndex - 1];
      setState({ multiPromptActiveStep: newActiveStep });
    },

  resetMultiPromptActiveStep:
    () =>
    ({ setState }) => {
      setState({ multiPromptActiveStep: MULTI_PROMPT_TEMPLATE_STEPS[0] });
    },

  getCallToAction:
    () =>
    ({ getState }) => {
      return getState().callToAction;
    },

  updateCallToAction:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ callToAction: value });
      // Validate new annotation content
      const wordsValidation = validateCallToActionText(value);

      if (!wordsValidation.valid) {
        dispatch(setError('callToAction', wordsValidation.message));
        return;
      }

      dispatch(ACTIONS.clearCallToActionError());
    },

  // Multi prompt fields
  initMultiPromptFields:
    (multiPromptFieldData = {}) =>
    ({ setState }) => {
      if (!multiPromptFieldData) {
        return;
      }

      const multiPromptFields = {};
      multiPromptFieldData?.order?.forEach((multiPromptFieldKey) => {
        multiPromptFields[multiPromptFieldKey] = EMPTY_ALL_INSTRUCTIONS_DATA;
      });

      setState({ multiPromptFields });
    },

  setMultiPromptField:
    (multiPromptFieldName, value) =>
    ({ setState, getState }) => {
      const multiPromptFields = getState().multiPromptFields || {};
      setState({ multiPromptFields: { ...multiPromptFields, [multiPromptFieldName]: value } });
    },

  // Take only "images" from "attached" resources
  getMultiPromptFieldImages:
    () =>
    ({ getState }) => {
      const { attachedResourcesMap } = getState();

      const result = {};
      Object.keys(attachedResourcesMap).forEach((key) => {
        result[key] = attachedResourcesMap[key].filter(isImageResource);
      });

      return result;
    },

  // Take only non-"images" from "attached" resources
  getMultiPromptFieldDocuments:
    () =>
    ({ getState }) => {
      const { attachedResourcesMap } = getState();

      const result = {};
      Object.keys(attachedResourcesMap).forEach((key) => {
        result[key] = attachedResourcesMap[key].filter(isNonImageFileResource);
      });

      return result;
    },

  setMultiPromptFields:
    (multiPromptFields) =>
    ({ setState }) => {
      setState({ multiPromptFields });
    },

  getMultiPromptField:
    (multiPromptFieldName) =>
    ({ getState }) => {
      return getState().multiPromptFields?.[multiPromptFieldName];
    },

  getMultiPromptFieldsForGeneration:
    () =>
    ({ getState }) => {
      const fields = getState().multiPromptFields;
      const fieldsForGeneration = {};
      Object.keys(fields).forEach((field) => {
        const serialized = replaceFilePlaceholders(fields[field].serialized.trim(), fields, field);
        fieldsForGeneration[field] = {
          freestyleToolAllInstructionsData: fields[field],
          index: fields[field]['index'],
          text: serialized,
        };
      });
      return fieldsForGeneration;
    },

  setCustomMultiPromptId:
    (id) =>
    ({ setState }) => {
      setState({ customMultiPromptId: id });
    },

  getCustomMultiPromptId:
    () =>
    ({ getState }) => {
      return getState().customMultiPromptId;
    },

  setChainId:
    (chainId) =>
    ({ setState }) => {
      setState({ chainId });
    },

  setCsvChainId:
    (csvChainId) =>
    ({ setState }) => {
      setState({ csvChainId });
    },

  getChainId:
    () =>
    ({ getState }) => {
      return getState().chainId;
    },

  getCsvChainId:
    () =>
    ({ getState }) => {
      return getState().csvChainId;
    },

  setToolPrompts:
    (toolPrompts) =>
    ({ setState }) => {
      setState({ toolPrompts });
    },

  getToolPrompts:
    () =>
    ({ getState }) => {
      const { toolPrompts } = getState();
      return toolPrompts;
    },

  updateToolPromptData:
    (blockId, data) =>
    ({ setState, getState }) => {
      const { toolPrompts } = getState();
      if (!toolPrompts) return;

      const newValue = produce(toolPrompts, (draft) => {
        const toolPrompt = draft.find((tp) => tp.blockId === blockId);
        if (toolPrompt) {
          Object.assign(toolPrompt, data);
        }
      });

      setState({ toolPrompts: newValue });
    },

  updateToolPromptIdByIndex:
    (index, id) =>
    ({ setState, getState }) => {
      const toolPrompts = getState().toolPrompts;
      const updatedToolPrompts = toolPrompts.map((toolPrompt, idx) => {
        if (idx === parseInt(index)) {
          return { ...toolPrompt, blockId: id };
        } else {
          return toolPrompt;
        }
      });
      setState({ toolPrompts: updatedToolPrompts });
    },

  openManagePersonasPopup:
    () =>
    ({ setState }) => {
      setState({ isManagePersonasPopupOpen: true });
    },
  closeManagePersonasPopup:
    () =>
    ({ setState }) => {
      setState({ isManagePersonasPopupOpen: false });
    },

  addAttachedResource:
    (promptId, resource, uploadId = null) =>
    ({ setState, getState }) => {
      const { attachedResourcesMap } = getState();

      const newMap = produce(attachedResourcesMap, (draft) => {
        draft[promptId] = draft[promptId] || [];
        // Replace "uploading" placeholder with the uploaded resource ...
        if (uploadId) {
          const index = draft[promptId].findIndex((r) => r.id === uploadId);
          draft[promptId][index] = resource;
        } else if (!draft[promptId].find((r) => r.id === resource.id)) {
          // ... or add the resource to the list if it doesn't exist there yet
          draft[promptId].push(resource);
        }
      });

      setState({ attachedResourcesMap: newMap });
    },

  removeAttachedResource:
    (promptId, id) =>
    ({ setState, getState }) => {
      const { attachedResourcesMap } = getState();

      const newMap = produce(attachedResourcesMap, (draft) => {
        if (draft[promptId]) {
          draft[promptId] = draft[promptId].filter((document) => document.id !== id);
        }
      });

      setState({ attachedResourcesMap: newMap });
    },

  getIsImprovedPrompt:
    () =>
    ({ getState }) => {
      return getState().freestyleToolContent?.improvedPrompt;
    },

  getGenerationToolOptionsCache:
    () =>
    ({ getState }) => {
      return getState().generationToolOptionsCache;
    },

  isGenerating:
    () =>
    ({ getState }) => {
      return getState().generating;
    },

  setIsCsvLoading:
    (isCsvLoading) =>
    ({ setState }) => {
      setState({ isCsvLoading });
    },

  setUploadedCsv:
    (uploadedCsv) =>
    ({ setState }) => {
      setState({ uploadedCsv });
    },

  uploadCsv:
    (csvFile) =>
    async ({ dispatch }, { customer, notifications }) => {
      dispatch(ACTIONS.setIsCsvLoading(true));
      try {
        const csv = await uploadCsvFile({
          csv: csvFile,
          customerId: customer.id,
        });
        dispatch(ACTIONS.setUploadedCsv(csv));
      } catch (error) {
        let errorMessage = 'There was an error uploading the file';
        if (error?.message === 'CSV can have a maximum of 100 rows.') {
          errorMessage = 'File exceeds the 100-row limit';
        }
        if (error?.message.startsWith('Invalid CSV encoding or illegal characters')) {
          errorMessage = error.message;
        }
        notifications.displayError({
          message: errorMessage,
        });
      } finally {
        dispatch(ACTIONS.setIsCsvLoading(false));
      }
    },

  getCsvId:
    () =>
    ({ getState }) => {
      return getState().uploadedCsv?.id;
    },

  getStructureInstructionsData:
    () =>
    ({ getState }) => {
      return getState().structureInstructionsData;
    },

  updateStructureInstructionsData:
    (value) =>
    ({ setState }) => {
      setState({ structureInstructionsData: value });
    },

  clearStructureInstructionsData:
    () =>
    ({ getState, setState }) => {
      const { subGenerationToolsProps } = getState();
      const newSubProps = produce(subGenerationToolsProps, (draft) => {
        Object.values(draft).forEach((subProps) => {
          Object.assign(subProps, {
            structureInstructionsData: null,
          });
        });
      });

      setState({
        structureInstructionsData: null,
        subGenerationToolsProps: newSubProps,
      });

      // If we clearing "structure instructions", then we probably don't need "selected vocabulary" info as well
      setState({ lastSelectedBrandVocabularyIds: null });
    },

  getSubGenerationToolStructure:
    (subGenerationTool) =>
    ({ getState }) => {
      const props = getState().subGenerationToolsProps[subGenerationTool];
      return props?.structureInstructionsData;
    },

  updateSubGenerationToolStructure:
    (subGenerationTool, value) =>
    ({ setState, getState }) => {
      const { subGenerationToolsProps: currentProps } = getState();
      if (!currentProps[subGenerationTool]) {
        return;
      }

      const newProps = produce(currentProps, (draft) => {
        draft[subGenerationTool].structureInstructionsData = value;
      });
      setState({ subGenerationToolsProps: newProps });
    },

  getLastSelectedBrandVocabularyIds:
    () =>
    ({ getState }) => {
      return getState().lastSelectedBrandVocabularyIds;
    },

  setLastSelectedBrandVocabularyIds:
    (value) =>
    ({ setState }) => {
      setState({ lastSelectedBrandVocabularyIds: value });
    },

  toggleImageGeneration:
    () =>
    ({ getState, setState }) => {
      const { isImageGenerationEnabled: currentValue } = getState();
      setState({ isImageGenerationEnabled: !currentValue });
    },
  changeImageGeneration:
    (isImageGenerationEnabled) =>
    ({ setState }) => {
      setState({ isImageGenerationEnabled });
    },
  isImageGenerationEnabled:
    () =>
    ({ getState }) => {
      return getState().isImageGenerationEnabled;
    },

  validateSubTemplateProperties:
    () =>
    ({ dispatch, getState }) => {
      const { subTemplateProperties } = getState();

      if (!dispatch(ACTIONS.isSubTemplateSelected())) {
        return true;
      }

      // Checks if mandatory questions have a value
      const isValid = subTemplateProperties.qaInputs.some((input) => input.mandatory);

      if (!isValid) {
        dispatch(setError('subTemplateProperties', 'Input needed'));
        return false;
      }

      return true;
    },
  getSubTemplateId:
    () =>
    ({ getState }) => {
      return getState().subTemplateId;
    },

  isSubTemplateSelected:
    () =>
    ({ getState }) => {
      const { subTemplateProperties } = getState();

      return Boolean(subTemplateProperties.id);
    },

  getSubTemplateProperties:
    () =>
    ({ getState }) => {
      const { subTemplateProperties } = getState();
      return {
        ...subTemplateProperties,
        qaInputs: subTemplateProperties.qaInputs.map((input) => {
          const { mandatory, ...rest } = input;
          return rest;
        }),
      };
    },

  setInitialFreestyleToolContent:
    ({ freestyleToolContent }) =>
    ({ setState }) => {
      setState({ initialFreestyleToolContent: freestyleToolContent });
    },

  resetInitialFreestyleToolContent:
    () =>
    ({ setState }) => {
      setState({ initialFreestyleToolContent: null });
    },

  setInitialSubTemplateProperties:
    ({ allInstructionsData, question, subTemplateId }) =>
    ({ setState }) => {
      if (!subTemplateId) {
        return;
      }

      setState({
        initialSubTemplateProperties: {
          id: subTemplateId,
          qaInputs: [
            {
              allInstructionsData,
              question: question.text,
              mandatory: question.mandatory,
            },
          ],
        },
      });
    },

  resetInitialSubTemplateProperties:
    () =>
    ({ setState }) => {
      setState({ initialSubTemplateProperties: null });
    },

  initSubTemplateProperties:
    () =>
    ({ dispatch, setState, getState }) => {
      const { subTemplateId, initialSubTemplateProperties } = getState();

      if (initialSubTemplateProperties) {
        setState({ subTemplateProperties: initialSubTemplateProperties });
        dispatch(ACTIONS.resetInitialSubTemplateProperties());
      } else {
        setState({
          subTemplateProperties: {
            ...EMPTY_SUB_TEMPLATE_PROPERTIES,
            id: subTemplateId && subTemplateId !== PROMPT_SUB_TEMPLATE_ID ? subTemplateId : null,
          },
        });
      }
    },
  setSubTemplateProperties:
    ({ content, allInstructionsData, question, subTemplateId }) =>
    ({ dispatch, getState, setState }) => {
      if (!subTemplateId) {
        return;
      }

      const { subTemplateProperties } = getState();

      const qaInputs = subTemplateProperties.qaInputs || [];
      const isQuestionExists = qaInputs.find((input) => input.question === question.text);

      if (isQuestionExists) {
        const newQaInputs = qaInputs.map((input) =>
          input.question === question.text ? { ...input, allInstructionsData } : input
        );

        setState({
          subTemplateProperties: {
            id: subTemplateId,
            qaInputs: newQaInputs,
          },
        });
      } else {
        setState({
          subTemplateProperties: {
            id: subTemplateId,
            qaInputs: [
              ...qaInputs,
              {
                allInstructionsData,
                question: question.text,
                mandatory: question.mandatory, // This field will be removed on collecting params, used for validation
              },
            ],
          },
        });
      }

      if (!content) {
        const newQaInputs = qaInputs.filter((input) => input.question !== question.text);

        setState({
          subTemplateProperties: {
            id: subTemplateId,
            qaInputs: newQaInputs,
          },
        });
      }

      if (question.mandatory) {
        dispatch(setError('subTemplateProperties', null));
      }
    },
  suggestQuestionsForText:
    (customer, options) =>
    async ({ getState, setState }, { notifications }) => {
      setState({ isSuggestPromptLoading: true });
      let urlExtractions = [];
      let briefExtraction;

      try {
        if (options?.briefExtractionPromise) {
          briefExtraction = await options?.briefExtractionPromise;
          if (briefExtraction?.blockingErrors) {
            return { briefExtractionResult: briefExtraction };
          }

          const extractedBriefs = briefExtraction?.extractedBriefs;
          if (extractedBriefs && Object.keys(extractedBriefs).length > 0) {
            // This is legacy support, to avoid risk the url is used somewhere else in the system (tracking events, for e.g.)
            urlExtractions = Object.entries(extractedBriefs).map(([url, extractedBrief]) => ({
              url,
              extractedBrief,
            }));
          }
        }

        const result = await suggestQuestionsForText({
          customerId: customer.id,
          allInstructionsData: JSON.stringify(getState().freestyleToolContent.allInstructionsData),
          urlExtractions,
        });

        return result.questions;
      } catch (error) {
        //in case of error, go back to not using suggested prompt as none was created.
        notifications.displayError(error);
      } finally {
        setState({ isSuggestPromptLoading: false });
      }
    },
  initQuestionsPopover:
    (questions) =>
    ({ setState, dispatch }) => {
      setState({ questions });
      dispatch(ACTIONS.openQuestionsPopover());
    },
  resetQuestionsPopover:
    () =>
    ({ setState, dispatch }) => {
      setState({
        questions: [],
        allInstructionsDataPerQuestion: {},
        isSuggestPromptQuestionsLoading: false,
      });
      dispatch(ACTIONS.closeQuestionsPopover());
    },
  isQuestionInitialized:
    () =>
    ({ getState }) => {
      return !!getState().questions.length;
    },
  openQuestionsPopover:
    () =>
    ({ setState }) => {
      setState({ isQuestionPopoverOpen: true });
    },
  closeQuestionsPopover:
    () =>
    ({ setState }) => {
      setState({ isQuestionPopoverOpen: false });
    },
  setAllInstructionsDataForQuestion:
    (question, allInstructionsData) =>
    async ({ getState, setState }) => {
      setState({
        allInstructionsDataPerQuestion: {
          ...getState().allInstructionsDataPerQuestion,
          [question.text]: allInstructionsData,
        },
      });
    },
  getAllInstructionsDataPerQuestion:
    () =>
    ({ getState }) => {
      return getState().allInstructionsDataPerQuestion;
    },
  moveAttachedResourcesFromQuestions:
    () =>
    ({ getState, setState }) => {
      const { questions, attachedResourcesMap } = getState();

      // TODO: @Stas Use flatMap to replace all resources at once

      const newMap = produce(attachedResourcesMap, (draft) => {
        // initialize Prompt resources
        draft[SINGLE_PROMPT_ID] = draft[SINGLE_PROMPT_ID] || [];

        // Copy resources from Questions to Prompt
        questions.forEach((question) => {
          const promptId = question.text;
          draft[promptId] = draft[promptId] || [];

          draft[promptId].forEach((resource) => {
            if (!draft[SINGLE_PROMPT_ID].find((r) => r.id === resource.id)) {
              draft[SINGLE_PROMPT_ID].push(resource);
            }
          });

          // Clean resources for Question
          draft[promptId] = [];
        });
      });

      setState({ attachedResourcesMap: newMap });
    },
  incrementTemplateDataVersion:
    () =>
    ({ setState, getState }) => {
      const { templateDataVersion } = getState();
      setState({ templateDataVersion: templateDataVersion + 1 });
    },

  /* MULTI PROMPT TEMPLATE ACTIONS */
  getIsSubToolsExpanded:
    () =>
    ({ getState }) => {
      return getState().isSubToolsExpanded;
    },
  setIsSubToolsExpanded:
    (value) =>
    ({ setState }) => {
      setState({ isSubToolsExpanded: value });
    },
  getActiveSubToolBlock:
    () =>
    ({ getState }) => {
      return getState().activeSubToolBlock;
    },
  setActiveSubToolBlock:
    (block) =>
    ({ setState }) => {
      setState({ activeSubToolBlock: block });
    },
  getMultiPromptTemplateSubToolProps:
    () =>
    ({ getState }) => {
      const { multiPromptTemplateAdvancedSettings } = getState();
      if (!multiPromptTemplateAdvancedSettings) return [];

      return multiPromptTemplateAdvancedSettings.subToolProps;
    },
  getMultiPromptTemplateAdvancedSettings:
    () =>
    ({ getState }) => {
      return getState().multiPromptTemplateAdvancedSettings;
    },
  getMultiPromptTemplateAdvancedSettingsDraft:
    () =>
    ({ getState }) => {
      return getState().multiPromptTemplateAdvancedSettingsDraft;
    },
  setMultiPromptTemplateAdvancedSettings:
    (value) =>
    ({ setState }) => {
      setState({ multiPromptTemplateAdvancedSettings: value });
    },
  setMultiPromptTemplateAdvancedSettingsDraft:
    (value) =>
    ({ setState }) => {
      setState({ multiPromptTemplateAdvancedSettingsDraft: value });
    },
  applyMultiPromptTemplateAdvancedSettings:
    () =>
    ({ getState, setState }) => {
      const { multiPromptTemplateAdvancedSettingsDraft } = getState();
      setState({ multiPromptTemplateAdvancedSettings: multiPromptTemplateAdvancedSettingsDraft });
    },
  resetMultiPromptTemplateAdvancedSettingsDraft:
    () =>
    ({ getState, setState }) => {
      const { multiPromptTemplateAdvancedSettings } = getState();
      setState({ multiPromptTemplateAdvancedSettingsDraft: multiPromptTemplateAdvancedSettings });
    },
  cascadeMultiPromptTemplateSubToolProperty:
    ({ value, property, fieldType, isInitialSync = false }) =>
    ({ getState, setState, dispatch }) => {
      const { multiPromptTemplateAdvancedSettingsDraft } = getState();
      if (!multiPromptTemplateAdvancedSettingsDraft?.subToolProps) {
        return;
      }

      const updatedSubToolProps = multiPromptTemplateAdvancedSettingsDraft.subToolProps.map(
        (item) => {
          // do not cascade if field type is not matching with tool type (e.g. do not apply image-related setting to text-only tool and vice versa)
          const isImageTool = isImageGenerationTool(item.generationTool);
          if (
            (isImageTool && fieldType !== FIELD_TYPE.image) ||
            (!isImageTool && fieldType === FIELD_TYPE.image)
          ) {
            return item;
          }

          if (fieldType === FIELD_TYPE.text) {
            if (property === 'structureInstructionsData' && isMixtureTool(item.generationTool)) {
              const updatedSubGenerationToolsProps = mapValues(
                item.subGenerationToolsProps,
                (subTool) => ({ ...subTool, structureInstructionsData: value })
              );

              return { ...item, subGenerationToolsProps: updatedSubGenerationToolsProps };
            }

            return { ...item, [property]: value };
          }

          if (fieldType === FIELD_TYPE.image) {
            return { ...item, [property]: value };
          }

          return item;
        }
      );

      setState({
        multiPromptTemplateAdvancedSettingsDraft: {
          ...multiPromptTemplateAdvancedSettingsDraft,
          subToolProps: updatedSubToolProps,
        },
      });

      dispatch(ACTIONS.incrementTemplateDataVersion());

      // structureInstructionsData initial value should be applied only on initial sync
      if (isInitialSync && property === 'structureInstructionsData') {
        dispatch(ACTIONS.applyMultiPromptTemplateAdvancedSettings());
      }

      // structureInstructionsData should be applied only when confirming the advanced settings
      if (property !== 'structureInstructionsData') {
        dispatch(ACTIONS.applyMultiPromptTemplateAdvancedSettings());
      }
    },
  /* ADVANCED SETTINGS DRAFT */
  setAdvancedSettingsDraft:
    (advancedSettingsDraft) =>
    ({ setState }) => {
      setState({ advancedSettingsDraft });
    },
  getAdvancedSettingsDraft:
    () =>
    ({ getState }) => {
      return getState().advancedSettingsDraft;
    },

  /* ADVANCED SETTINGS POPOVER ACTIONS*/
  getAdvancedSettingsActiveViewProps:
    () =>
    ({ getState }) => {
      return getState().advancedSettingsActiveViewProps;
    },
  setAdvancedSettingsActiveViewProps:
    (viewProps) =>
    ({ setState }) => {
      setState({ advancedSettingsActiveViewProps: viewProps });
    },
  resetAdvancedSettingsActiveViewProps:
    () =>
    ({ setState }) => {
      setState({ advancedSettingsActiveViewProps: INITIAL_ADVANCED_SETTINGS_VIEW_PROPS });
    },
};

export const LocalStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'view-generation-form',
});

const prepareExtractedFromAssetSections = (sections) => {
  const getContentText = (content) => {
    if (isHtmlContent(content)) {
      return convertHtmlContentToPlainText(content);
    }

    return content;
  };

  return sections.map((section) => ({
    ...section,
    // convert assetTypes to generationTools
    generationTool: getSectionToolFromAssetType(section.assetType),
    content: getContentText(section.content),
  }));
};

const createRewriteInputSectionFromBackendMapper = (contentType) => (section, index, sections) => {
  const prepareSectionName = () => {
    // use provided name if possible
    if (section.name) {
      return section.name;
    }
    const baseGenerationTool = getShortFormContentTypeTool(contentType);
    const assetType = getToolDefaultAssetType(section.generationTool); // asset-types generally have shorter labels
    let label;

    if (baseGenerationTool) {
      label = getToolMixtureSubAssetTypeSubtitles(baseGenerationTool, assetType); // try to use lable from base generation tool
    }

    label =
      label ||
      getAssetLabel(assetType) ||
      getToolLabel(section.generationTool) ||
      section.generationTool;

    // some content types require section numbers in the names
    if (shortFormContentTypeHasSectionNumbering(contentType)) {
      const sameTypeSectionsBefore = sections
        .slice(0, index)
        .filter((s) => s.generationTool === section.generationTool);
      const sectionNumber = sameTypeSectionsBefore.length + 1;
      return `${label} ${sectionNumber}`;
    }

    // or use localized tool name as default
    return label;
  };

  const result = {
    ...section,
    // make sure each section has its own unique ID, either from BE or generated here
    id: section.id || generateLocalSectionId(),
    // use provided name or localize asset name is possible
    name: prepareSectionName(),
  };

  // in case we are constructing section from backend section data, mimic image resource object
  if (section.imageResourceId && section.path) {
    result.imageResource = mimicImageResource({
      imageResourceId: section.imageResourceId,
      path: section.path,
    });
  }

  // image section should not be improved by default (but not in "image" content type)
  if (
    isImageGenerationTool(section.generationTool) &&
    contentType !== SHORT_FORM_CONTENT_TYPE.generalImage
  ) {
    result.improveParams = {
      improveMode: GENERATION_IMPROVE_MODE.doNotImprove,
    };
  }

  return result;
};

export const generateLocalSectionId = () => {
  return uniqueId('local_section_');
};
